import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { pushKukaSoittaaLeadEventToDataLayer } from '../../common/analytics.js';
import { useSubmitLead } from '../../common/hooks/useSubmitLead.js';
import { t } from '../../common/i18n/index.js';
import { getStoredParametersCookie } from '../../common/utils/gaTrackingUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { Lead } from '../../generated/api/models.js';
import { CustomFormThankYouForOrder, CustomFormType, createPrices } from './CustomFormFields.js';
import { CorporateWebHostingForm, defaultHostingOption } from './forms/CorporateWebHostingForm.js';
import { DialogiMessagingServiceForm } from './forms/DialogiMessagingServiceForm.js';
import { DomainNameForm } from './forms/DomainNameForm.js';
import { KukaSoittaaLeadForm } from './forms/KukaSoittaaLeadForm.js';
import { LeadForm } from './forms/LeadForm.js';
import { Microsoft365Form } from './forms/Microsoft365Form.js';
import type { PostAnonymousLeadRequest } from '../../generated/api/models.js';
import type { CustomFormData } from '../cmsSchema.js';
import type { PriceMaps } from './CustomFormFields.js';

import './CustomForm.scss';

export enum CustomFormIds {
  KUKASOITTAA_LEAD_FORM = 'KUKASOITTAA_LEAD_FORM',
  CORPORATE_WEB_HOSTING = 'CORPORATE_WEBHOSTING',
  DIALOGI_MESSAGING_SERVICE = 'DIALOGI_MESSAGING_SERVICE',
  DOMAIN_NAME = 'DOMAIN_NAME',
  LEAD_FORM = 'LEAD_FORM',
  MICROSOFT_365 = 'MICROSOFT_365',
}

export interface CustomFormProps extends CustomFormData {
  leadInProgress?: boolean;
  leadFailed?: boolean;
  leadSent?: boolean;
  enableCompanyFields?: boolean;
  // TODO are these actually used for anything?
  initialValues: PriceMaps;
}

export interface CustomFormDataForLead {
  businessId: string;
  businessName: string;
  consentForMarketing?: boolean;
  email: string;
  firstName: string;
  interestArea?: Lead.InterestAreaEnum;
  lastName: string;
  message: string;
  phoneNumber: string;
}

// These are _only_ used by CorporateWebHostingForm
export const createInitialNonEmptyValues = (id: string) => {
  const periodicPriceMap = new Map();
  const onetimePriceMap = new Map();
  if (id === CustomFormIds.CORPORATE_WEB_HOSTING) {
    const defaultItem = defaultHostingOption();
    if (defaultItem) {
      if (defaultItem.oneTimeCharge) {
        onetimePriceMap?.set(CustomFormType.OPTION, defaultItem.oneTimeCharge);
      }
      if (defaultItem.monthlyRecurringCharge) {
        periodicPriceMap?.set(CustomFormType.OPTION, defaultItem.monthlyRecurringCharge);
      }
    }
  }

  return createPrices(periodicPriceMap, onetimePriceMap);
};

const mapLeadFormDataToLeadRequest = (values: CustomFormDataForLead, campaignId?: string): PostAnonymousLeadRequest => {
  if (!isInBrowser()) {
    throw new Error('Not in browser environment. Unable to send lead-form.');
  }
  const storedParametersCookie = getStoredParametersCookie();
  const referrer: string =
    typeof window !== 'undefined' && typeof document !== 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).google_tag_manager?.[(window as any).config?.googleTagManagerId]?.dataLayer?.get?.(
          'page_referrer'
        ) ||
        document.referrer ||
        ''
      : '';
  return {
    contactDetails: {
      businessId: values.businessId,
      businessName: values.businessName,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
    },
    lead: {
      ...(campaignId !== '' && campaignId != null ? { campaignId } : {}),
      ...(storedParametersCookie ? storedParametersCookie : {}),
      consentForMarketing: Boolean(values.consentForMarketing),
      consentToContact: true,
      description: values.message,
      interestArea: campaignId ? Lead.InterestAreaEnum.CAMPAIGN_DEFINED_AREA : values.interestArea,
      leadType: Lead.LeadTypeEnum.ONLINE_CONTACT,
      sourceUrl: window.location.toString(),
      sourcePathPreviousPageUrl: referrer,
    },
  };
};

const mapFormParameters = (data: CustomFormDataForLead) => {
  const formParameters = {};
  const filterList = [
    'businessId',
    'businessName',
    'companySearch',
    'domainSearch',
    'email',
    'phoneNumber',
    'firstName',
    'lastName',
    'onetimePriceMap',
    'periodicPriceMap',
  ];

  for (const [key, value] of Object.entries(data)) {
    if (filterList.indexOf(key) === -1) {
      if (typeof value === 'object') {
        if (value instanceof Map) {
          // @ts-ignore
          formParameters[key] = JSON.stringify(Object.entries(Object.fromEntries(value)));
        } else {
          if (key === CustomFormType.M365) {
            // This is required because the format in which m365 data is passed on is a bit specific
            const m365Value = Object.entries(value);
            // @ts-ignore
            formParameters[key] = JSON.stringify(m365Value);
          } else {
            // @ts-ignore
            formParameters[key] = JSON.stringify(value);
          }
        }
      } else {
        // @ts-ignore
        formParameters[key] = value;
      }
    }
  }
  return formParameters;
};

const mapCustomFormDataToLeadRequest = (data: CustomFormDataForLead): PostAnonymousLeadRequest => {
  if (!isInBrowser()) {
    throw new Error('Not in browser environment. Unable to send custom-form.');
  }
  return {
    contactDetails: {
      businessId: data.businessId,
      businessName: data.businessName,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
    },
    lead: {
      formParameters: mapFormParameters(data),
      leadType: Lead.LeadTypeEnum.ONLINE_ORDER,
      sourceUrl: window.location.href,
    },
  };
};

const CustomFormElement = (props: CustomFormProps) => {
  const { id } = props;
  switch (id) {
    case CustomFormIds.KUKASOITTAA_LEAD_FORM:
      return <KukaSoittaaLeadForm {...props} />;
    case CustomFormIds.MICROSOFT_365:
      return <Microsoft365Form {...props} />;
    case CustomFormIds.DOMAIN_NAME:
      return <DomainNameForm {...props} />;
    case CustomFormIds.CORPORATE_WEB_HOSTING:
      return <CorporateWebHostingForm {...props} />;
    case CustomFormIds.DIALOGI_MESSAGING_SERVICE:
      return <DialogiMessagingServiceForm {...props} />;
    case CustomFormIds.LEAD_FORM:
      return <LeadForm {...props} />;
    default:
      return <></>;
  }
};

const CustomFormComponent = (props: CustomFormProps) => {
  const { campaignId, thankYouForOrderMessage } = props;
  const { leadState, submitLead } = useSubmitLead();

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      ...{
        businessId: '',
        businessName: '',
        email: '',
        existingDomain: '',
        firstName: '',
        lastName: '',
        mainUserEmail: '',
        mainUserName: '',
        mainUserPhone: '',
        message: '',
        phoneNumber: '',
        [CustomFormType.M365]: [],
      },
    },
  });

  const scrollTo = useRef<HTMLInputElement>(null);
  const executeScroll = () => scrollTo?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const onSubmit = async (values: CustomFormDataForLead) => {
    const request =
      props.id === CustomFormIds.LEAD_FORM || props.id === CustomFormIds.KUKASOITTAA_LEAD_FORM
        ? mapLeadFormDataToLeadRequest(values, campaignId)
        : mapCustomFormDataToLeadRequest(values);
    if (await submitLead(request)) {
      if (props.id === CustomFormIds.KUKASOITTAA_LEAD_FORM) {
        pushKukaSoittaaLeadEventToDataLayer();
      }
      executeScroll();
    }
  };

  return (
    <div ref={scrollTo}>
      {/* KukaSoittaa has custom layout & thankyou message, handled in KukaSoittaaLeadForm */}
      {leadState === 'SUCCESS' && props.id !== CustomFormIds.KUKASOITTAA_LEAD_FORM ? (
        <CustomFormThankYouForOrder message={thankYouForOrderMessage ?? t.O4R6('Thank you for your order!')} />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <CustomFormElement
              leadInProgress={leadState === 'IN_PROGRESS'}
              leadFailed={leadState === 'FAIL'}
              leadSent={leadState === 'SUCCESS'}
              {...props}
            />
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export const CustomForm = (data: CustomFormData) => {
  return <CustomFormComponent {...data} initialValues={createInitialNonEmptyValues(data.id)} />;
};
